import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ProcedureParameterConfirmationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ClientId} from 'notebook/ELNConstants';
import {downloadCSVActionFunction} from 'notebook/structured-tables/action-functions/downloadCSVActionFunction';
import {CustomColumnDataType} from 'notebook/structured-tables/custom-columns/CustomColumnConstants';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  ActionFunctions,
  BaseStructuredTable,
  CreateStructuredTableProps,
  LastSyncedUpstreamValue,
  SchemaAndColumnGroup,
  StructuredTableAdapterOptions,
  StructuredTableColumn,
  StructuredTableKind,
  StructuredTableRow,
  StructuredTableSchema,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {EvaluatedCellError} from 'notebook/tables/excess/Evaluator';
import {
  BaseTypedValueType,
  Cell,
  RowMetadata,
  Table,
  TableColumnType,
  TableDimension,
  TableReorderLocks,
  TypedValue,
} from 'notebook/tables/TableConstants';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {ProcedureSchemaFieldTypeTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {PARAMETER_TABLES_COLUMN_WIDTHS} from 'procedures/design/ProcedureDesignConstants';
import {
  ProcedureValidationError,
  ProcedureValidationErrorType,
  ProcedureValidationObjectType,
} from 'procedures/design/validation/ProcedureValidationTypes';
import {filterProcedureValidationErrors} from 'procedures/design/validation/procedureValidationUtils';
import {
  getMethodComponentCid,
  MaterialFlow,
  ParameterUsage,
  ParameterUsages,
  ProcedureMaterialInputPlannedValue,
  ProcedureMaterialInputSetting,
} from 'procedures/ProcedureDomainTypes';
import {
  DEFAULT_MATERIAL_TYPE,
  MaterialType,
} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';
import {
  convertMaterialTypeToSchemaFieldDataType,
  convertMaterialTypeToTypedValue,
  convertTypedValueToMaterialType,
  SchemaHydrationError,
} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldTypeConversionUtils';
import {
  BaseParameterConfigTableMetadata,
  getParameterUsageColumn,
  getParameterUsagesForId,
  makeParameterUsageCell,
} from 'procedures/tables/parameterTableAdapterParamUsageUtils';
import {
  cellFloat,
  cellText,
  getConfirmationMethodStructuredTableColumn,
  getConfirmationMethodValueFromCell,
  getUserAnnotationIndicator,
  inferPlannedTextOrBioentityLinkTypedValue,
  makeConfirmationMethodCell,
  makeEntityLinkCell,
  makeStringCell,
  makeTextSchemaFieldCell,
  makeUnitReferenceCellFromUnit,
  validationErrorsToEvaluatedCellError,
} from 'procedures/tables/procedureTableAdapterUtils';
import {ProcedureSchemaFieldTypeContext} from 'procedures/tables/ProcedureTableConstants';
import {FieldType, isRequiredLinkTagSchema} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {Optional} from 'util/Types';

import {TagSchemaListingWithFieldsGraphQL} from '../../../graphql-fragments/schemas/__generated/TagSchemaListingWithFieldsGraphQL';

export interface ProcedureMaterialInputsTableMetadata extends BaseParameterConfigTableMetadata {}

export interface ProcedureMaterialInputsTableRowMetadata {
  apiId: string | null;
  procedureMaterialInputId: string | null;
  plannedValueId: string | null;
}

export enum MaterialInputConfigColumn {
  NAME = 'NAME',
  TYPE = 'TYPE',
  DEFAULT_MATERIAL = 'DEFAULT_MATERIAL',
  AMOUNT = 'AMOUNT',
  AMOUNT_UNIT = 'AMOUNT_UNIT',
  MAPPED_FROM = 'MAPPED_FROM',
  DESCRIPTION = 'DESCRIPTION',
  CONFIRMATION_METHOD = 'CONFIRMATION_METHOD',
  CONFIRMED_IN = 'CONFIRMED_IN',
}

export const ALL_POSSIBLE_COLUMNS_ORDERED = [
  MaterialInputConfigColumn.NAME,
  MaterialInputConfigColumn.TYPE,
  MaterialInputConfigColumn.DEFAULT_MATERIAL,
  MaterialInputConfigColumn.AMOUNT,
  MaterialInputConfigColumn.AMOUNT_UNIT,
  MaterialInputConfigColumn.MAPPED_FROM,
  MaterialInputConfigColumn.DESCRIPTION,
  MaterialInputConfigColumn.CONFIRMATION_METHOD,
  MaterialInputConfigColumn.CONFIRMED_IN,
];

const GET_IS_ENABLED_BY_COLUMN: {[c in MaterialInputConfigColumn]?: () => boolean} = {
  [MaterialInputConfigColumn.CONFIRMATION_METHOD]: () =>
    BenchlingConfig.PROCEDURES_ENABLE_USE_PARAMETERS_IN_PLATES,
};

export function columnProvenanceId(column: MaterialInputConfigColumn): string {
  return `mi_${column}`;
}

const GET_STC_BY_COLUMN: Record<
  MaterialInputConfigColumn,
  (metadata: ProcedureMaterialInputsTableMetadata) => StructuredTableColumn
> = {
  [MaterialInputConfigColumn.NAME]: () => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.STRING,
      name: 'Name',
      isRequired: false,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.WIDE,
    }),
    provenanceUuid: columnProvenanceId(MaterialInputConfigColumn.NAME),
  }),
  [MaterialInputConfigColumn.TYPE]: () => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE,
      name: 'Material type',
      isRequired: true,
      defaultValue: {
        text: '',
        typedValue: convertMaterialTypeToTypedValue(DEFAULT_MATERIAL_TYPE),
      },
      width: PARAMETER_TABLES_COLUMN_WIDTHS.WIDE,
    }),
    columnType: TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE, // for TS
    context: ProcedureSchemaFieldTypeContext.MATERIAL_INPUT,
    provenanceUuid: columnProvenanceId(MaterialInputConfigColumn.TYPE),
  }),
  [MaterialInputConfigColumn.DEFAULT_MATERIAL]: () => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.ROW_DELEGATE,
      name: 'Default material',
      isRequired: false,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.NORMAL,
    }),
    provenanceUuid: columnProvenanceId(MaterialInputConfigColumn.DEFAULT_MATERIAL),
  }),
  [MaterialInputConfigColumn.AMOUNT]: () => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.CUSTOM,
      name: 'Amount',
      isRequired: false,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.NARROW,
    }),
    columnType: TableColumnType.CUSTOM,
    provenanceUuid: columnProvenanceId(MaterialInputConfigColumn.AMOUNT),
    customColumnConfig: {dataType: CustomColumnDataType.FLOAT},
  }),
  [MaterialInputConfigColumn.AMOUNT_UNIT]: () => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.UNIT_REFERENCE,
      name: 'Units',
      isRequired: false,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.NORMAL,
    }),
    provenanceUuid: columnProvenanceId(MaterialInputConfigColumn.AMOUNT_UNIT),
  }),
  [MaterialInputConfigColumn.MAPPED_FROM]: () => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.STRING,
      name: 'Mapped from',
      isRequired: false,
      isReadOnly: true,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.WIDE,
    }),
    provenanceUuid: columnProvenanceId(MaterialInputConfigColumn.MAPPED_FROM),
  }),
  [MaterialInputConfigColumn.DESCRIPTION]: () => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.STRING,
      name: 'Description',
      isRequired: false,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.WIDE,
    }),
    provenanceUuid: columnProvenanceId(MaterialInputConfigColumn.DESCRIPTION),
  }),
  [MaterialInputConfigColumn.CONFIRMED_IN]: (metadata) =>
    getParameterUsageColumn({
      metadata,
      provenanceUuid: columnProvenanceId(MaterialInputConfigColumn.CONFIRMED_IN),
    }),
  [MaterialInputConfigColumn.CONFIRMATION_METHOD]: () => ({
    ...getConfirmationMethodStructuredTableColumn(),
    provenanceUuid: columnProvenanceId(MaterialInputConfigColumn.CONFIRMATION_METHOD),
  }),
};

export function getColumnIndex(
  targetColumn: MaterialInputConfigColumn,
  stColumns: Array<StructuredTableColumn>
): number {
  return stColumns.findIndex(({provenanceUuid}) => provenanceUuid === columnProvenanceId(targetColumn));
}

const MAPPED_DEFAULT_MATERIAL_MESSAGE =
  'This material is passed from a previous unit operation. In order to set a default material, ' +
  'you must delete the arrow linking the materials.';

const MAPPED_MATERIAL_TYPE_MESSAGE =
  'This material is passed from a previous unit operation. In order to set a different type, ' +
  'you must delete the arrow linking the materials.';

function makeCellLockedMessageForMappedCells(
  message: string,
  mapping?: MaterialFlow
): {cellLockedMessage: string} | undefined {
  return mapping ? {cellLockedMessage: message} : undefined;
}

/**
 * This table adapter is for configuration of material inputs
 */
export type ProcedureMaterialInputsTable = BaseStructuredTable<
  ProcedureMaterialInputsTableMetadata,
  ProcedureMaterialInputsTableRowMetadata,
  ClientId
>;

export default class ProcedureMaterialInputsTableAdapter extends GenericStructuredTableAdapter<
  ProcedureMaterialInputsTableMetadata,
  ProcedureMaterialInputsTableRowMetadata
> {
  constructor(options?: StructuredTableAdapterOptions) {
    super(StructuredTableKind.PROCEDURE_MATERIAL_INPUTS, {allowNoRowsInEntries: true, ...options});
  }

  getActionFunctions(
    tableMetadata: ProcedureMaterialInputsTableMetadata,
    schemas: Array<StructuredTableSchema>
  ): ActionFunctions<ProcedureMaterialInputsTableMetadata, ProcedureMaterialInputsTableRowMetadata> {
    return {secondary: [downloadCSVActionFunction]};
  }

  getDisplayName(table: ProcedureMaterialInputsTable): string {
    return '';
  }

  getRowDisplayName(_: ProcedureMaterialInputsTable): string {
    return 'material';
  }

  getBlankRowMetadata(
    tableMetadata: ProcedureMaterialInputsTableMetadata
  ): ProcedureMaterialInputsTableRowMetadata {
    return {apiId: null, procedureMaterialInputId: null, plannedValueId: null};
  }

  static getAdapter(): ProcedureMaterialInputsTableAdapter {
    return getStructuredTableAdapters()[
      StructuredTableKind.PROCEDURE_MATERIAL_INPUTS
    ] as ProcedureMaterialInputsTableAdapter;
  }

  allowPasteTable(): boolean {
    return false;
  }

  allowDeleteTable(_: ProcedureMaterialInputsTable): {
    canDelete: boolean;
    reason?: string;
  } {
    return {canDelete: false};
  }

  _getAllSupportedColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string;
    metadata: ProcedureMaterialInputsTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    const enabledColumns = ALL_POSSIBLE_COLUMNS_ORDERED.filter(
      (c) => GET_IS_ENABLED_BY_COLUMN[c]?.() ?? true
    );
    return enabledColumns.map((c) => GET_STC_BY_COLUMN[c](metadata));
  }

  // We don't lock any columns in a finalized row
  getNonFinalizingColumnTypes(): Array<TableColumnType> {
    return [
      ...super.getNonFinalizingColumnTypes(),
      TableColumnType.STRING,
      TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE,
      TableColumnType.CUSTOM,
      TableColumnType.UNIT_REFERENCE,
      TableColumnType.BIOENTITY_SELECTOR,
      TableColumnType.OPTION_SELECTOR,
      TableColumnType.PROCEDURE_PARAMETER_CONFIRMED_IN,
    ];
  }

  // Check against all user-entered fields. Will never report "empty" if there is a backing model;
  // this deliberately prevents users from blanking out fields on an existing object.
  static isMaterialInputEmpty(mi: ProcedureMaterialInputSetting): boolean {
    const setpoint = mi.values[0];
    return (
      mi.materialInputVersion.id == null &&
      mi.materialInputVersion.name.length === 0 &&
      mi.materialInputVersion.description.length === 0 &&
      formatTypedValue(setpoint.material).length === 0 &&
      mi.materialInputVersion.confirmationMethod === ProcedureParameterConfirmationMethod.TABLE
    );
  }

  skipUnsubmittedIndicatorsForRow(
    structuredTable: ProcedureMaterialInputsTable,
    row: StructuredTableRow<ProcedureMaterialInputsTableRowMetadata>
  ): boolean {
    try {
      // This function will throw if a material input setting links to a tag schema which is not provided. We don't
      // have access to the tag schemas here, but it's fine: all we're doing is assessing whether or not the
      // material input setting is empty.
      const setting = ProcedureMaterialInputsTableAdapter.rowToMaterialInputSetting(
        structuredTable.columns,
        row,
        []
      );
      return ProcedureMaterialInputsTableAdapter.isMaterialInputEmpty(setting);
    } catch (e) {
      // If we get here, the material input setting points to a schema, so it's not considered empty. We defer
      // to the default behavior for this row.
      if (!(e instanceof SchemaHydrationError)) {
        throw e;
      }
    }
    return false;
  }

  static getDelegateColumnByColumnAndMaterialType(
    this: void,
    dependentColumn: StructuredTableColumn,
    materialType: MaterialType
  ): StructuredTableColumn {
    switch (materialType.type) {
      case FieldType.TEXT:
        return {
          ...makeStructuredTableColumn({
            columnType: TableColumnType.CUSTOM,
            name: 'Delegate: text',
            isRequired: false,
          }),
          columnType: TableColumnType.CUSTOM,
          // The provenance ID must be stable. It's used to track the LSUV for any cell which uses this column
          provenanceUuid: `${dependentColumn.provenanceUuid}_${materialType.type}`,
          customColumnConfig: {dataType: CustomColumnDataType.TEXT},
        };
      case FieldType.BIOENTITY_LINK: {
        const provenanceUuidSchemaSuffix = isRequiredLinkTagSchema(materialType.requiredLink)
          ? `-${materialType.requiredLink.tagSchema.id}`
          : '';
        return {
          ...makeStructuredTableColumn({
            columnType: TableColumnType.BIOENTITY_SELECTOR,
            name: 'Delegate: bioentity selector',
            isRequired: false,
          }),
          columnType: TableColumnType.BIOENTITY_SELECTOR,
          provenanceUuid: `${dependentColumn.provenanceUuid}_${materialType.type}${provenanceUuidSchemaSuffix}`,
          schemaFieldForFiltering: convertMaterialTypeToSchemaFieldDataType(materialType, false),
        };
      }
      default:
        assert(false, `Unknown material type: ${materialType.type}`, {materialType});
    }
  }

  private static makeMaterialTypeInputCell(
    materialInputSetting: ProcedureMaterialInputSetting,
    allValidationErrors: Array<ProcedureValidationError>,
    options?: {cellLockedMessage?: Optional<string>; error?: Optional<string>}
  ): Cell {
    const cellLockedMessage = options?.cellLockedMessage;
    const userAnnotationIndicator = getUserAnnotationIndicator(cellLockedMessage);
    return {
      text: '',
      typedValue: convertMaterialTypeToTypedValue(materialInputSetting.materialInputVersion.dataType),
      userAnnotationIndicator,
      error: validationErrorsToEvaluatedCellError(
        getMethodComponentCid(materialInputSetting),
        ProcedureValidationObjectType.MATERIAL_INPUT,
        filterProcedureValidationErrors(allValidationErrors, {
          errorTypes: [
            ProcedureValidationErrorType.INVALID_SCHEMA_FOR_PARAMETER_TYPE,
            ProcedureValidationErrorType.NON_ENTITY_MATERIAL_INPUT_CONFIRMED_VIA_PLATE_MAP,
            ProcedureValidationErrorType.UNCONTAINABLE_SCHEMA_ON_MATERIAL_INPUT_CONFIRMED_VIA_PLATE_MAP,
          ],
        })
      ),
      locked: cellLockedMessage != null,
    };
  }

  private static makeAmountCell(
    materialInputPlannedValue: ProcedureMaterialInputPlannedValue | null,
    options: {error: Optional<EvaluatedCellError>} = {
      error: null,
    }
  ): Cell {
    const {error} = options;
    return {
      text: materialInputPlannedValue?.amount?.value ?? '',
      typedValue: materialInputPlannedValue?.amount ?? null,
      error,
    };
  }

  static makeCellFromMaterialInputSetting(
    materialInputSetting: ProcedureMaterialInputSetting,
    materialInputPlannedValue: ProcedureMaterialInputPlannedValue | null,
    column: StructuredTableColumn,
    validationErrors: Array<ProcedureValidationError>,
    materialFlows: Array<MaterialFlow>,
    parameterUsages: Array<ParameterUsage>
  ): Cell {
    const materialMapping = (): MaterialFlow | undefined =>
      materialFlows.find(
        ({targetMaterialCid}) => targetMaterialCid === materialInputSetting.materialInputVersion.cid
      );

    switch (column.provenanceUuid) {
      case columnProvenanceId(MaterialInputConfigColumn.NAME): {
        const nameCellError = validationErrorsToEvaluatedCellError(
          materialInputSetting.materialInputVersion.cid,
          ProcedureValidationObjectType.MATERIAL_INPUT,
          filterProcedureValidationErrors(validationErrors, {
            errorTypes: [
              ProcedureValidationErrorType.NON_UNIQUE_NAME,
              ProcedureValidationErrorType.INVALID_NAME,
            ],
          })
        );

        return makeStringCell(materialInputSetting.materialInputVersion.name, {
          error: nameCellError,
        });
      }

      case columnProvenanceId(MaterialInputConfigColumn.TYPE):
        return ProcedureMaterialInputsTableAdapter.makeMaterialTypeInputCell(
          materialInputSetting,
          validationErrors,
          makeCellLockedMessageForMappedCells(MAPPED_MATERIAL_TYPE_MESSAGE, materialMapping())
        );

      case columnProvenanceId(MaterialInputConfigColumn.DEFAULT_MATERIAL): {
        const materialInputDefaultMaterial = materialInputPlannedValue?.material;
        const materialMappingInfoForDefaultMaterial = makeCellLockedMessageForMappedCells(
          MAPPED_DEFAULT_MATERIAL_MESSAGE,
          materialMapping()
        );
        const defaultMaterialCellError = validationErrorsToEvaluatedCellError(
          materialInputSetting.materialInputVersion.cid,
          ProcedureValidationObjectType.MATERIAL_INPUT,
          filterProcedureValidationErrors(validationErrors, {
            errorTypes: [ProcedureValidationErrorType.MISMATCHED_VALUE_TYPE],
          })
        );
        return materialInputDefaultMaterial?.type === FieldType.BIOENTITY_LINK &&
          materialInputDefaultMaterial.targetFiles?.length > 0
          ? makeEntityLinkCell(
              [
                {
                  id: materialInputDefaultMaterial?.targetFiles[0].id ?? '',
                  name: materialInputDefaultMaterial?.targetFiles[0].name,
                  url: materialInputDefaultMaterial?.targetFiles[0].url,
                },
              ],
              {
                ...materialMappingInfoForDefaultMaterial,
                error: defaultMaterialCellError,
              }
            )
          : makeTextSchemaFieldCell(materialInputDefaultMaterial?.value, {
              ...materialMappingInfoForDefaultMaterial,
              error: defaultMaterialCellError,
            });
      }

      case columnProvenanceId(MaterialInputConfigColumn.AMOUNT): {
        const amountCellError = validationErrorsToEvaluatedCellError(
          materialInputSetting.materialInputVersion.cid,
          ProcedureValidationObjectType.MATERIAL_INPUT,
          filterProcedureValidationErrors(validationErrors, {
            errorTypes: [ProcedureValidationErrorType.NEGATIVE_AMOUNT],
          })
        );

        return ProcedureMaterialInputsTableAdapter.makeAmountCell(materialInputPlannedValue, {
          error: amountCellError,
        });
      }

      case columnProvenanceId(MaterialInputConfigColumn.AMOUNT_UNIT):
        return makeUnitReferenceCellFromUnit(materialInputSetting.materialInputVersion.amountUnit);

      case columnProvenanceId(MaterialInputConfigColumn.MAPPED_FROM): {
        const mappedMaterial = materialMapping();
        return makeStringCell(
          mappedMaterial ? `${mappedMaterial.sourceMethodName}: ${mappedMaterial.sourceMaterialName}` : null
        );
      }

      case columnProvenanceId(MaterialInputConfigColumn.DESCRIPTION):
        return makeStringCell(materialInputSetting.materialInputVersion.description);

      case columnProvenanceId(MaterialInputConfigColumn.CONFIRMED_IN):
        return makeParameterUsageCell(parameterUsages, {
          confirmationMethod: materialInputSetting.materialInputVersion.confirmationMethod,
        });

      case columnProvenanceId(MaterialInputConfigColumn.CONFIRMATION_METHOD):
        return makeConfirmationMethodCell(
          materialInputSetting.materialInputVersion.confirmationMethod ??
            // Adding a new row sets every cell to an empty typed value, we want it to default to table
            ProcedureParameterConfirmationMethod.TABLE,
          validationErrorsToEvaluatedCellError(
            getMethodComponentCid(materialInputSetting),
            ProcedureValidationObjectType.MATERIAL_INPUT,
            filterProcedureValidationErrors(validationErrors, {
              errorTypes: [
                ProcedureValidationErrorType.NON_ENTITY_MATERIAL_INPUT_CONFIRMED_VIA_PLATE_MAP,
                ProcedureValidationErrorType.UNCONTAINABLE_SCHEMA_ON_MATERIAL_INPUT_CONFIRMED_VIA_PLATE_MAP,
              ],
            })
          )
        );

      default:
        assert(false, 'Unknown table column', {column});
    }
  }

  static materialInputSettingToRow(
    materialInputSetting: ProcedureMaterialInputSetting,
    table: ProcedureMaterialInputsTable,
    rowIndex: number,
    validationErrors: Array<ProcedureValidationError>,
    materialFlows: Array<MaterialFlow>,
    parameterUsages: Array<ParameterUsage>,
    cellLockedMessage?: Optional<string>
  ): StructuredTableRow<ProcedureMaterialInputsTableRowMetadata> {
    const adapter = ProcedureMaterialInputsTableAdapter.getAdapter();
    const materialInputPlannedValue =
      materialInputSetting.values.length > 0 ? materialInputSetting.values[0] : null;

    return {
      ...adapter.getBlankRow(table.metadata, rowIndex, table.columns),
      rowId: materialInputSetting.materialInputVersion.cid ?? uuidv4(),
      metadata: {
        apiId: materialInputSetting.materialInputVersion.id,
        procedureMaterialInputId: materialInputSetting.materialInputVersion.procedureMaterialInputId,
        plannedValueId: materialInputPlannedValue?.id ?? null,
      },
      cells: table.columns.map((column) =>
        ProcedureMaterialInputsTableAdapter.makeCellFromMaterialInputSetting(
          materialInputSetting,
          materialInputPlannedValue,
          column,
          validationErrors,
          materialFlows,
          parameterUsages
        )
      ),
    };
  }

  private static isProcedureSchemaFieldTypeTypedValue(
    typedValue: TypedValue
  ): typedValue is ProcedureSchemaFieldTypeTypedValue {
    return typedValue.type === BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE;
  }

  private static isBioentityLinkFieldType(fieldType: FieldType): fieldType is FieldType.BIOENTITY_LINK {
    return fieldType === FieldType.BIOENTITY_LINK;
  }

  /**
   * Cell parsing must be done with minimal demands on correctness. This allows for unexpected table
   * interactions like paste or the delete key. Here we simply force any invalid value to TEXT type.
   */
  private static getMaterialTypeFromCell(
    cell: Cell,
    tagSchemas: Array<TagSchemaListingWithFieldsGraphQL>
  ): MaterialType {
    const typedValue = cell.typedValue;
    if (
      typedValue != null &&
      ProcedureMaterialInputsTableAdapter.isProcedureSchemaFieldTypeTypedValue(typedValue) &&
      ProcedureMaterialInputsTableAdapter.isBioentityLinkFieldType(
        typedValue.fieldDefinition.fieldType as FieldType
      )
    ) {
      return convertTypedValueToMaterialType(typedValue, tagSchemas);
    }
    return DEFAULT_MATERIAL_TYPE;
  }

  static rowToMaterialInputSetting(
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<ProcedureMaterialInputsTableRowMetadata>,
    tagSchemas: Array<TagSchemaListingWithFieldsGraphQL>
  ): ProcedureMaterialInputSetting {
    assert(
      columns.length === row.cells.length,
      'Expected row to have same number of cells as number of columns'
    );
    const materialTypeCell = row.cells[getColumnIndex(MaterialInputConfigColumn.TYPE, columns)];
    const materialType = ProcedureMaterialInputsTableAdapter.getMaterialTypeFromCell(
      materialTypeCell,
      tagSchemas
    );

    const amountCell = row.cells[getColumnIndex(MaterialInputConfigColumn.AMOUNT, columns)];
    const amountUnitReferenceCell = row.cells[getColumnIndex(MaterialInputConfigColumn.AMOUNT_UNIT, columns)];

    const amountUnit =
      amountUnitReferenceCell.typedValue?.type === BaseTypedValueType.UNIT_REFERENCE
        ? amountUnitReferenceCell.typedValue.unitSummary
        : null;

    const defaultMaterialCell =
      row.cells[getColumnIndex(MaterialInputConfigColumn.DEFAULT_MATERIAL, columns)];

    const confirmationMethod = BenchlingConfig.PROCEDURES_ENABLE_USE_PARAMETERS_IN_PLATES
      ? getConfirmationMethodValueFromCell(
          row.cells[getColumnIndex(MaterialInputConfigColumn.CONFIRMATION_METHOD, columns)]
        )
      : ProcedureParameterConfirmationMethod.TABLE;

    return {
      materialInputVersion: {
        cid: row.rowId,
        id: row.metadata.apiId,
        procedureMaterialInputId: row.metadata.procedureMaterialInputId,
        name: cellText(row.cells[getColumnIndex(MaterialInputConfigColumn.NAME, columns)]).trim(),
        description: cellText(
          row.cells[getColumnIndex(MaterialInputConfigColumn.DESCRIPTION, columns)]
        ).trim(),
        dataType: materialType,
        amountUnit,
        confirmationMethod,
      },
      values: [
        {
          id: row.metadata.plannedValueId,
          cid: `${row.rowId}-planned-value`,
          material: inferPlannedTextOrBioentityLinkTypedValue(materialType, defaultMaterialCell),

          // It is a bit surprising that `amountCell` only has a text property and not a typedValue here.
          // But given that, we just make a float value field type from the specified text value.
          amount: {
            type: FieldType.FLOAT,
            value: cellText(amountCell),
            floatValue: cellFloat(amountCell) ?? null,
          },
        },
      ],
    };
  }

  static makeLastSyncedUpstreamValues(
    this: void,
    materialInputSetting: ProcedureMaterialInputSetting,
    row: StructuredTableRow<ProcedureMaterialInputsTableRowMetadata>,
    columns: Array<StructuredTableColumn>,
    parameterUsages: Array<ParameterUsage>
  ): Array<LastSyncedUpstreamValue> {
    const submittedAt = new Date().toISOString();
    const materialInputPlannedValue =
      materialInputSetting.values.length > 0 ? materialInputSetting.values[0] : null;

    return columns.map((column) => {
      const delegatedColumnProvenanceId = (row.metadata as RowMetadata).delegatedColumns?.[
        column.provenanceUuid
      ];
      return {
        columnGroup: null,
        columnType: column.columnType,
        columnProvenanceUuid: delegatedColumnProvenanceId ?? column.provenanceUuid,
        submittedAt,
        fieldId: null,
        value:
          ProcedureMaterialInputsTableAdapter.makeCellFromMaterialInputSetting(
            materialInputSetting,
            materialInputPlannedValue,
            column,
            [],
            [],
            parameterUsages
          ).typedValue ?? null,
      };
    });
  }

  toTable(materialsTable: ProcedureMaterialInputsTable): Table {
    const table = super.toTable(materialsTable);
    return {
      ...table,
      lockedDims: {allowAddRows: true, allowRemoveRows: true, allowEditCols: false},
      allowColumnEdits: false,
    };
  }

  createTableFromMaterials(
    createProps: CreateStructuredTableProps<ProcedureMaterialInputsTableMetadata>,
    tableCid: string,
    materialInputs: Array<ProcedureMaterialInputSetting>,
    materialFlows: Array<MaterialFlow>,
    procedureValidationErrors: Array<ProcedureValidationError>,
    parameterUsagesByParameterVersionId: ParameterUsages
  ): ProcedureMaterialInputsTable {
    const baseTable = {
      ...this.createTable(createProps),
      cid: tableCid,
    };
    return {
      ...baseTable,
      rows: materialInputs.map((material, index) =>
        ProcedureMaterialInputsTableAdapter.materialInputSettingToRow(
          material,
          baseTable,
          index,
          procedureValidationErrors,
          materialFlows,
          getParameterUsagesForId(parameterUsagesByParameterVersionId, material.materialInputVersion.id),
          null
        )
      ),
    };
  }

  allowReorderDimensions(): TableReorderLocks {
    return {
      [TableDimension.ROW]: true,
      [TableDimension.COLUMN]: false,
    };
  }
}
